import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    Brain,
    Briefcase,
    CheckCircle2,
    Circle,
    Clock,
    FileText,
    Link2,
    ListTodo,
    Loader2,
    Mail,
    Send,
    Sparkles,
    Tag,
    Trash2,
    User,
    Zap,
    FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DURUM_OPTIONS = [
    { v: "ACIK", l: "Açık" },
    { v: "BEKLEMEDE", l: "Beklemede" },
    { v: "COZULDU", l: "Çözüldü" },
    { v: "ESKALE", l: "Eskale" },
];

const ONCELIK_OPTIONS = [
    { v: "NORMAL", l: "Normal" },
    { v: "YUKSEK", l: "Yüksek" },
    { v: "ACIL", l: "Acil" },
];

const TABS = [
    { id: "ozet", label: "Özet", icon: Sparkles },
    { id: "dosya", label: "Dosya", icon: FileText },
    { id: "gecmis", label: "Geçmiş", icon: Clock },
    { id: "gorevler", label: "Görevler", icon: ListTodo },
    { id: "bagla", label: "Bağla", icon: Link2 },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function fmtCountdown(saniye) {
    if (saniye == null) return "";
    const abs = Math.abs(saniye);
    const sa = Math.floor(abs / 3600);
    const dk = Math.floor((abs % 3600) / 60);
    const txt = sa > 0 ? `${sa}s ${dk}dk` : `${dk}dk`;
    return saniye < 0 ? `${txt} gecikti` : `${txt} kaldı`;
}

function RiskBadge({ risk }) {
    if (!risk) return null;
    const cls =
        risk.seviye === "kritik"
            ? "wa-crm__risk wa-crm__risk--kritik"
            : risk.seviye === "yuksek"
              ? "wa-crm__risk wa-crm__risk--yuksek"
              : risk.seviye === "orta"
                ? "wa-crm__risk wa-crm__risk--orta"
                : "wa-crm__risk";
    return (
        <span className={cls} title={(risk.nedenler || []).join(", ")}>
            Risk {risk.puan}
        </span>
    );
}

function ProgressBar({ progress }) {
    if (!progress) return null;
    const pct = progress.percent || 0;
    return (
        <div className="wa-crm__progress">
            <div className="wa-crm__progress-head">
                <span>{progress.current_label || "—"}</span>
                <span>%{pct}</span>
            </div>
            <div className="wa-crm__progress-track">
                <div className="wa-crm__progress-fill" style={{ width: `${pct}%` }} />
            </div>
            {(progress.steps || []).length > 0 && (
                <div className="wa-crm__pipeline">
                    {(progress.steps || []).slice(0, 6).map((s) => (
                        <span
                            key={s.code}
                            className={`wa-crm__pipe-step wa-crm__pipe-step--${s.state}`}
                            title={s.label}
                        >
                            {s.state === "done" ? (
                                <CheckCircle2 className="h-3 w-3" />
                            ) : s.state === "current" ? (
                                <Circle className="h-3 w-3 fill-current" />
                            ) : (
                                <Circle className="h-3 w-3 opacity-40" />
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function WhatsAppInboxCrmPanel({ konusmaKey, konusma, onMetaUpdated }) {
    const [crm, setCrm] = useState(null);
    const [kullanicilar, setKullanicilar] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({});
    const [yeniEtiket, setYeniEtiket] = useState("");
    const [tab, setTab] = useState("ozet");
    const [slaTick, setSlaTick] = useState(0);

    const [muvekkilQ, setMuvekkilQ] = useState("");
    const [muvekkilOpts, setMuvekkilOpts] = useState([]);
    const [secMuvekkil, setSecMuvekkil] = useState("");
    const [dosyaOpts, setDosyaOpts] = useState([]);
    const [secDosya, setSecDosya] = useState("");
    const [herZamanDosya, setHerZamanDosya] = useState(false);
    const [baglaBusy, setBaglaBusy] = useState(false);

    const [gorevForm, setGorevForm] = useState({ baslik: "", aciklama: "", hatirlatma_tarihi: "" });
    const [gorevBusy, setGorevBusy] = useState(false);

    const load = useCallback(async () => {
        if (!konusmaKey) return;
        setLoading(true);
        try {
            const [crmRes, metaRes, usersRes] = await Promise.all([
                whatsappMerkez.inboxCrm(konusmaKey),
                whatsappMerkez.inboxKonusmaMetaGet(konusmaKey),
                kullanicilar.length ? Promise.resolve({ kullanicilar }) : whatsappMerkez.inboxKullanicilar(),
            ]);
            setCrm(crmRes);
            setMeta(metaRes.meta || crmRes.meta || {});
            if (usersRes?.kullanicilar) setKullanicilar(usersRes.kullanicilar);
            const bag = crmRes?.baglanti;
            if (bag) {
                setSecMuvekkil(bag.muvekkil_id ? String(bag.muvekkil_id) : "");
                setSecDosya(bag.varsayilan_dosya_id ? String(bag.varsayilan_dosya_id) : "");
                setHerZamanDosya(!!bag.her_zaman_bu_dosya);
            }
        } catch {
            toast.error("CRM kartı yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [konusmaKey, kullanicilar.length]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const t = setInterval(() => setSlaTick((x) => x + 1), 30000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!secMuvekkil) {
            setDosyaOpts([]);
            return;
        }
        whatsappMerkez.lookupDosyalar({ muvekkil_id: secMuvekkil, limit: 30 }).then((res) => {
            setDosyaOpts(res?.dosyalar || []);
        });
    }, [secMuvekkil]);

    const slaText = useMemo(() => {
        void slaTick;
        const c = crm?.sla_countdown;
        if (!c) return null;
        return fmtCountdown(c.kalan_saniye);
    }, [crm?.sla_countdown, slaTick]);

    async function patchMeta(payload) {
        if (!konusmaKey) return;
        try {
            const res = await whatsappMerkez.inboxKonusmaMetaPatch(konusmaKey, payload);
            if (res?.meta) {
                setMeta(res.meta);
                onMetaUpdated?.(res.meta);
                setCrm((c) => (c ? { ...c, meta: res.meta } : c));
            }
        } catch {
            toast.error("Güncellenemedi.");
        }
    }

    async function sablonGonder(kod) {
        if (!konusmaKey || !kod) return;
        try {
            const res = await whatsappMerkez.inboxSablonGonder(konusmaKey, kod);
            if (res?.ok) toast.success("Şablon gönderildi.");
            else toast.error(res?.detail || "Gönderilemedi.");
        } catch {
            toast.error("Şablon gönderilemedi.");
        }
    }

    async function vekaletGonder() {
        if (!konusmaKey) return;
        try {
            const res = await whatsappMerkez.inboxVekaletGonder(konusmaKey);
            if (res?.ok) toast.success("Vekalet örneği PDF gönderildi.");
            else toast.error(res?.detail || "Gönderilemedi.");
        } catch {
            toast.error("Vekalet gönderilemedi.");
        }
    }

    async function uygulaOneri(oneri) {
        const aks = oneri?.aksiyon || {};
        if (aks.tab) {
            setTab(aks.tab);
            return;
        }
        if (aks.sablon_kod) {
            await sablonGonder(aks.sablon_kod);
            return;
        }
        const patch = {};
        if (aks.durum) patch.durum = aks.durum;
        if (aks.oncelik) patch.oncelik = aks.oncelik;
        if (Object.keys(patch).length) await patchMeta(patch);
    }

    function etiketEkle(t) {
        const tag = (t || yeniEtiket).trim();
        if (!tag) return;
        const next = [...(meta.etiketler || []), tag].slice(0, 10);
        patchMeta({ etiketler: next });
        setYeniEtiket("");
    }

    async function araMuvekkil() {
        const q = muvekkilQ.trim();
        if (q.length < 2) return;
        const res = await whatsappMerkez.lookupMuvekkiller(q);
        setMuvekkilOpts(res?.muvekkiller || []);
    }

    async function baglaKaydet() {
        if (!konusmaKey) return;
        setBaglaBusy(true);
        try {
            const res = await whatsappMerkez.inboxCrmBagla({
                konusma_anahtar: konusmaKey,
                muvekkil_id: secMuvekkil ? Number(secMuvekkil) : null,
                varsayilan_dosya_id: secDosya ? Number(secDosya) : null,
                her_zaman_bu_dosya: herZamanDosya,
            });
            if (res?.ok) {
                toast.success("Bağlantı kaydedildi.");
                load();
            } else toast.error(res?.detail || "Kaydedilemedi.");
        } catch {
            toast.error("Bağlantı kaydedilemedi.");
        } finally {
            setBaglaBusy(false);
        }
    }

    async function gorevOlustur(e) {
        e?.preventDefault();
        if (!gorevForm.baslik.trim()) return;
        setGorevBusy(true);
        try {
            const res = await whatsappMerkez.inboxCrmGorevOlustur(konusmaKey, {
                ...gorevForm,
                atanan_id: meta.atanan_id || null,
            });
            if (res?.ok) {
                toast.success("Görev eklendi.");
                setGorevForm({ baslik: "", aciklama: "", hatirlatma_tarihi: "" });
                load();
            } else toast.error(res?.detail || "Eklenemedi.");
        } catch {
            toast.error("Görev eklenemedi.");
        } finally {
            setGorevBusy(false);
        }
    }

    async function gorevTamamla(id) {
        try {
            await whatsappMerkez.inboxCrmGorevGuncelle(id, { durum: "TAMAMLANDI" });
            load();
        } catch {
            toast.error("Güncellenemedi.");
        }
    }

    async function gorevSil(id) {
        try {
            await whatsappMerkez.inboxCrmGorevSil(id);
            load();
        } catch {
            toast.error("Silinemedi.");
        }
    }

    if (!konusmaKey) {
        return (
            <aside className="wa-crm wa-crm--empty">
                <p>Konuşma seçin — CRM kartı burada görünür.</p>
            </aside>
        );
    }

    if (loading && !crm) {
        return (
            <aside className="wa-crm">
                <div className="wa-crm__loading">
                    <Loader2 className="h-5 w-5 animate-spin" />
                </div>
            </aside>
        );
    }

    const dosya = crm?.dosya;
    const muvekkil = crm?.muvekkil;
    const m360 = crm?.muvekkil_360;
    const cokKanal = crm?.cok_kanal || {};

    return (
        <aside className="wa-crm wa-crm--gelismis">
            <div className="wa-crm__head">
                <Briefcase className="h-4 w-4" />
                <span>CRM & Operasyon</span>
                <RiskBadge risk={crm?.risk} />
                {meta.sla_ihlali || crm?.sla_countdown?.ihlal ? (
                    <span className="wa-crm__sla-badge">
                        <AlertTriangle className="h-3 w-3" /> SLA
                    </span>
                ) : slaText ? (
                    <span className="wa-crm__sla-ok">
                        <Clock className="h-3 w-3" /> {slaText}
                    </span>
                ) : null}
            </div>

            <div className="wa-crm__tabs">
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        className={`wa-crm__tab ${tab === id ? "wa-crm__tab--active" : ""}`}
                        onClick={() => setTab(id)}
                    >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            <div className="wa-crm__body">
                {tab === "ozet" && (
                    <>
                        {crm?.akil_ozet?.ozet && (
                            <div className="wa-crm__akil">
                                <Brain className="h-4 w-4 shrink-0" />
                                <p>{crm.akil_ozet.ozet}</p>
                            </div>
                        )}

                        {crm?.intent?.intent_label && (
                            <div className="wa-crm__intent">
                                <Zap className="h-3.5 w-3.5" />
                                Niyet: <strong>{crm.intent.intent_label}</strong>
                            </div>
                        )}

                        {(crm?.otomasyon_onerileri || []).length > 0 && (
                            <div className="wa-crm__section wa-crm__section--flat">
                                <label>Önerilen aksiyonlar</label>
                                <div className="wa-crm__oneri-list">
                                    {crm.otomasyon_onerileri.map((o) => (
                                        <button
                                            key={o.id}
                                            type="button"
                                            className="wa-crm__oneri"
                                            onClick={() => uygulaOneri(o)}
                                        >
                                            <Sparkles className="h-3 w-3 shrink-0" />
                                            <span>
                                                <strong>{o.baslik}</strong>
                                                <small>{o.aciklama}</small>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="wa-crm__grid2">
                            <div className="wa-crm__section wa-crm__section--compact">
                                <label>Durum</label>
                                <select
                                    value={meta.durum || "ACIK"}
                                    onChange={(e) => patchMeta({ durum: e.target.value })}
                                >
                                    {DURUM_OPTIONS.map((o) => (
                                        <option key={o.v} value={o.v}>
                                            {o.l}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="wa-crm__section wa-crm__section--compact">
                                <label>Öncelik</label>
                                <select
                                    value={meta.oncelik || "NORMAL"}
                                    onChange={(e) => patchMeta({ oncelik: e.target.value })}
                                >
                                    {ONCELIK_OPTIONS.map((o) => (
                                        <option key={o.v} value={o.v}>
                                            {o.l}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="wa-crm__section">
                            <label>Atanan</label>
                            <select
                                value={meta.atanan_id || ""}
                                onChange={(e) =>
                                    patchMeta({
                                        atanan_id: e.target.value ? Number(e.target.value) : null,
                                    })
                                }
                            >
                                <option value="">— Atanmadı —</option>
                                {kullanicilar.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.ad}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="wa-crm__section">
                            <label className="flex items-center gap-1">
                                <Tag className="h-3 w-3" /> Etiketler
                            </label>
                            <div className="wa-crm__tags">
                                {(meta.etiketler || []).map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        className="wa-crm__tag"
                                        onClick={() =>
                                            patchMeta({
                                                etiketler: (meta.etiketler || []).filter((x) => x !== t),
                                            })
                                        }
                                        title="Kaldır"
                                    >
                                        #{t} ×
                                    </button>
                                ))}
                            </div>
                            {(crm?.onerilen_etiketler || []).length > 0 && (
                                <div className="wa-crm__tag-suggest">
                                    {(crm.onerilen_etiketler || []).map((t) => (
                                        <button key={t} type="button" onClick={() => etiketEkle(t)}>
                                            +#{t}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="wa-crm__tag-add">
                                <input
                                    value={yeniEtiket}
                                    onChange={(e) => setYeniEtiket(e.target.value)}
                                    placeholder="Yeni etiket"
                                    onKeyDown={(e) => e.key === "Enter" && etiketEkle()}
                                />
                                <button type="button" onClick={() => etiketEkle()}>
                                    +
                                </button>
                            </div>
                        </div>

                        <div className="wa-crm__section">
                            <label>İç not</label>
                            <textarea
                                rows={2}
                                value={meta.ic_not || ""}
                                onChange={(e) => setMeta((m) => ({ ...m, ic_not: e.target.value }))}
                                onBlur={() => patchMeta({ ic_not: meta.ic_not || "" })}
                                placeholder="Müvekkile gitmez…"
                            />
                        </div>

                        {crm?.yanit_metrikleri && (
                            <div className="wa-crm__metrics">
                                {crm.yanit_metrikleri.bekleyen_saat != null && (
                                    <span>Bekleyen: {crm.yanit_metrikleri.bekleyen_saat}s</span>
                                )}
                                {crm.yanit_metrikleri.son_yanit_tarihi && (
                                    <span>Son yanıt: {fmtDate(crm.yanit_metrikleri.son_yanit_tarihi)}</span>
                                )}
                            </div>
                        )}

                        {(crm?.onerilen_sablonlar || crm?.hizli_sablonlar || []).length > 0 && (
                            <div className="wa-crm__section">
                                <label>Hızlı şablon</label>
                                <div className="wa-crm__quick">
                                    {(crm.onerilen_sablonlar || crm.hizli_sablonlar || [])
                                        .slice(0, 6)
                                        .map((s) => (
                                            <button
                                                key={s.kod}
                                                type="button"
                                                className="wa-crm__quick-btn"
                                                onClick={() => sablonGonder(s.kod)}
                                                title={s.metin_onizleme}
                                            >
                                                <Send className="h-3 w-3" />
                                                {s.ad || s.kod}
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="wa-crm__section">
                            <label>Vekalet</label>
                            <button
                                type="button"
                                className="wa-crm__vekalet-btn"
                                onClick={vekaletGonder}
                            >
                                <FileSignature className="h-4 w-4" />
                                Vekalet örneği gönder (PDF + bilgi)
                            </button>
                            <p className="wa-crm__muted mt-1">
                                Ahzukap yetkisi · en az 2 yıl süre uyarısı mesajda gönderilir (belgeye eklenmez).
                            </p>
                        </div>

                        {muvekkil ? (
                            <div className="wa-crm__card">
                                <div className="wa-crm__card-title">
                                    <User className="h-3.5 w-3.5" /> {muvekkil.ad}
                                    {muvekkil.whatsapp_onay ? (
                                        <span className="wa-crm__badge wa-crm__badge--ok">WA Onaylı</span>
                                    ) : (
                                        <span className="wa-crm__badge wa-crm__badge--warn">WA Onay yok</span>
                                    )}
                                </div>
                                <p className="wa-crm__muted">{muvekkil.telefon}</p>
                                {m360 && (
                                    <p className="wa-crm__muted">
                                        {m360.acik_dosya} açık / {m360.toplam_dosya} dosya
                                        {m360.kvkk_onay ? " · KVKK ✓" : ""}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <button type="button" className="wa-crm__link-btn" onClick={() => setTab("bagla")}>
                                Müvekkil bağlı değil — Bağla →
                            </button>
                        )}

                        {(cokKanal.son_eposta || cokKanal.son_panel_giris) && (
                            <div className="wa-crm__cok-kanal">
                                <label>Çok kanal</label>
                                {cokKanal.son_eposta && (
                                    <p>
                                        <Mail className="h-3 w-3 inline" /> {cokKanal.son_eposta.konu || "E-posta"}{" "}
                                        <small>{fmtDate(cokKanal.son_eposta.tarih)}</small>
                                    </p>
                                )}
                                {cokKanal.son_panel_giris && (
                                    <p>
                                        Panel giriş: <small>{fmtDate(cokKanal.son_panel_giris)}</small>
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}

                {tab === "dosya" && (
                    <>
                        {dosya ? (
                            <>
                                <div className="wa-crm__card">
                                    <div className="wa-crm__card-title">
                                        <FileText className="h-3.5 w-3.5" /> {dosya.dosya_no}
                                    </div>
                                    <p className="wa-crm__muted">
                                        {dosya.plaka} · {dosya.durum}
                                    </p>
                                    <ProgressBar progress={dosya.progress} />
                                    <a
                                        href={`/yeni/sigorta-dosyalari/${dosya.id}`}
                                        className="wa-crm__link"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Dosyayı aç →
                                    </a>
                                </div>

                                {(crm?.eksik_evrak_detay || []).length > 0 ? (
                                    <div className="wa-crm__section">
                                        <label>Eksik evrak</label>
                                        <ul className="wa-crm__eksik-actions">
                                            {(crm.eksik_evrak_detay || []).map((e) => (
                                                <li key={e.ad}>
                                                    <span>{e.ad}</span>
                                                    <button
                                                        type="button"
                                                        className="wa-crm__mini-btn"
                                                        onClick={() => sablonGonder(e.sablon_kod || "eksik_evrak")}
                                                    >
                                                        Hatırlat
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <p className="wa-crm__ok px-3">Eksik evrak yok</p>
                                )}

                                {(crm?.son_gonderimler || []).length > 0 && (
                                    <div className="wa-crm__section">
                                        <label>Son WhatsApp gönderimleri</label>
                                        <ul className="wa-crm__gonderim-list">
                                            {(crm.son_gonderimler || []).map((g) => (
                                                <li key={g.id}>
                                                    <span>{g.sablon_kod || "Mesaj"}</span>
                                                    <span className={`wa-crm__durum wa-crm__durum--${(g.durum || "").toLowerCase()}`}>
                                                        {g.durum}
                                                    </span>
                                                    <small>{fmtDate(g.tarih)}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="wa-crm__muted px-3">Dosya bağlı değil.</p>
                        )}

                        {m360?.dosyalar?.length > 0 && (
                            <div className="wa-crm__section">
                                <label>Müvekkil dosyaları</label>
                                <ul className="wa-crm__dosya-list">
                                    {m360.dosyalar.map((d) => (
                                        <li key={d.id}>
                                            <a href={`/yeni/sigorta-dosyalari/${d.id}`} target="_blank" rel="noreferrer">
                                                {d.dosya_no} · {d.plaka}
                                            </a>
                                            <span>%{d.percent}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}

                {tab === "gecmis" && (
                    <>
                        {(crm?.timeline || []).length > 0 && (
                            <div className="wa-crm__section">
                                <label>Dosya timeline</label>
                                <ul className="wa-crm__timeline">
                                    {(crm.timeline || []).map((ev, i) => (
                                        <li key={i}>
                                            <strong>{ev.title}</strong>
                                            {ev.description && <span>{ev.description}</span>}
                                            <small>{fmtDate(ev.tarih)}</small>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="wa-crm__section">
                            <label>CRM aktivite günlüğü</label>
                            {(crm?.aktiviteler || []).length > 0 ? (
                                <ul className="wa-crm__aktivite">
                                    {(crm.aktiviteler || []).map((a) => (
                                        <li key={a.id}>
                                            <strong>{a.kullanici_adi}</strong> {a.aciklama || a.aksiyon}
                                            <small>{fmtDate(a.tarih)}</small>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="wa-crm__muted">Henüz aktivite yok.</p>
                            )}
                        </div>
                    </>
                )}

                {tab === "gorevler" && (
                    <>
                        <form className="wa-crm__gorev-form" onSubmit={gorevOlustur}>
                            <input
                                placeholder="Görev başlığı"
                                value={gorevForm.baslik}
                                onChange={(e) => setGorevForm((f) => ({ ...f, baslik: e.target.value }))}
                            />
                            <input
                                type="datetime-local"
                                value={gorevForm.hatirlatma_tarihi}
                                onChange={(e) =>
                                    setGorevForm((f) => ({ ...f, hatirlatma_tarihi: e.target.value }))
                                }
                            />
                            <textarea
                                rows={2}
                                placeholder="Açıklama (opsiyonel)"
                                value={gorevForm.aciklama}
                                onChange={(e) => setGorevForm((f) => ({ ...f, aciklama: e.target.value }))}
                            />
                            <Button type="submit" size="sm" disabled={gorevBusy}>
                                {gorevBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Görev ekle"}
                            </Button>
                        </form>

                        <ul className="wa-crm__gorev-list">
                            {(crm?.gorevler || []).map((g) => (
                                <li
                                    key={g.id}
                                    className={`wa-crm__gorev ${g.gecikti ? "wa-crm__gorev--gecikti" : ""} ${g.durum === "TAMAMLANDI" ? "wa-crm__gorev--done" : ""}`}
                                >
                                    <div>
                                        <strong>{g.baslik}</strong>
                                        {g.aciklama && <p>{g.aciklama}</p>}
                                        <small>
                                            {g.atanan_adi && `${g.atanan_adi} · `}
                                            {g.hatirlatma_tarihi
                                                ? fmtDate(g.hatirlatma_tarihi)
                                                : fmtDate(g.olusturma_tarihi)}
                                        </small>
                                    </div>
                                    <div className="wa-crm__gorev-actions">
                                        {g.durum !== "TAMAMLANDI" && (
                                            <button type="button" onClick={() => gorevTamamla(g.id)} title="Tamamla">
                                                <CheckCircle2 className="h-4 w-4" />
                                            </button>
                                        )}
                                        <button type="button" onClick={() => gorevSil(g.id)} title="Sil">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {(crm?.gorevler || []).length === 0 && (
                            <p className="wa-crm__muted px-3">Açık görev yok.</p>
                        )}
                    </>
                )}

                {tab === "bagla" && (
                    <div className="wa-crm__section">
                        <label>Müvekkil ara</label>
                        <div className="wa-crm__tag-add">
                            <input
                                value={muvekkilQ}
                                onChange={(e) => setMuvekkilQ(e.target.value)}
                                placeholder="Ad, TC, telefon…"
                                onKeyDown={(e) => e.key === "Enter" && araMuvekkil()}
                            />
                            <button type="button" onClick={araMuvekkil}>
                                Ara
                            </button>
                        </div>
                        <select value={secMuvekkil} onChange={(e) => setSecMuvekkil(e.target.value)}>
                            <option value="">— Müvekkil seç —</option>
                            {muvekkilOpts.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.label} {m.telefon ? `· ${m.telefon}` : ""}
                                </option>
                            ))}
                            {secMuvekkil &&
                                !muvekkilOpts.find((m) => String(m.id) === secMuvekkil) &&
                                muvekkil && (
                                    <option value={muvekkil.id}>{muvekkil.ad}</option>
                                )}
                        </select>

                        <label className="mt-3">Varsayılan dosya</label>
                        <select value={secDosya} onChange={(e) => setSecDosya(e.target.value)}>
                            <option value="">— Otomatik —</option>
                            {dosyaOpts.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.label || d.dosya_no}
                                </option>
                            ))}
                        </select>

                        <label className="wa-crm__check mt-3">
                            <input
                                type="checkbox"
                                checked={herZamanDosya}
                                onChange={(e) => setHerZamanDosya(e.target.checked)}
                            />
                            Bu numaradan gelen evrakları varsayılan dosyaya kaydet
                        </label>

                        <Button className="mt-3 w-full" size="sm" onClick={baglaKaydet} disabled={baglaBusy}>
                            {baglaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bağlantıyı kaydet"}
                        </Button>
                    </div>
                )}
            </div>
        </aside>
    );
}
